import { auth } from '../firebase/config';

// Get Firebase Functions URL
const FUNCTIONS_URL = 'https://us-central1-mahstry.cloudfunctions.net';

class MahStryAITutorService {
  constructor() {
    this.conversations = new Map(); // Store conversations by sessionId with window of 10 messages
    this.maxMessages = 10; // ConversationBufferWindowMemory with k=10
  }

  // Initialize a new conversation session
  initializeSession(sessionId) {
    if (!this.conversations.has(sessionId)) {
      this.conversations.set(sessionId, {
        messages: [],
        createdAt: new Date(),
        lastActivity: new Date(),
      });
    }
    return this.conversations.get(sessionId);
  }

  // Get system prompt
  getSystemPrompt() {
    return `You are MahStry AI Tutor - a smart, concise educational assistant with vision capabilities.

**Core Rules:**
1. **Be CONCISE** - Give direct, brief answers unless user asks for "detailed explanation" or "step-by-step"
2. **Token Efficiency** - Keep responses short and focused
3. **Smart Responses:**
   - Simple questions → Brief answers (2-3 lines)
   - "Explain" or "How" → Moderate detail (4-6 lines)
   - "Detailed" or "Step-by-step" → Full explanation
4. **Image Analysis:**
   - When images are provided, analyze them carefully
   - Identify problems, diagrams, equations, or text in images
   - Provide solutions based on what you see
   - If it's a math problem, solve it step-by-step
   - If it's a diagram, explain what it shows
5. **Format:**
   - Use bullet points for lists
   - Use ** for bold text
   - Use \`code\` for formulas
   - Keep paragraphs short

**Examples:**
Q: "What is photosynthesis?"
A: "Process where plants convert sunlight, water, and CO2 into glucose and oxygen. Formula: 6CO2 + 6H2O + light → C6H12O6 + 6O2"

Q: "Explain photosynthesis in detail"
A: [Then provide detailed explanation]

Q: [Image of math problem]
A: "I can see the problem: [describe problem]. Here's the solution: [solve step-by-step]"

**Math Notation:**
- Exponents: x**2, a**n
- Fractions: a/b
- Square root: sqrt(x)
- Keep it simple and readable

**Your Style:**
- Friendly but brief
- Accurate and helpful
- Encourage questions for more detail
- End with "Need more detail? Just ask!"

Remember: Quality over quantity. Be smart with tokens!`;
  }

  // Send a message and get response
  async sendMessage(sessionId, message, context = {}) {
    try {
      const session = this.initializeSession(sessionId);
      session.lastActivity = new Date();

      // Add context to the message if provided
      let enhancedMessage = message;
      if (context.subject) {
        enhancedMessage = `[Subject: ${context.subject}] ${message}`;
      }

      // Prepare user message content
      let userMessageContent;
      
      // If images are provided, use vision format
      if (context.images && context.images.length > 0) {
        userMessageContent = [
          {
            type: 'text',
            text: enhancedMessage,
          },
          ...context.images.map(imageUrl => ({
            type: 'image_url',
            image_url: {
              url: imageUrl,
              detail: 'high', // Use high detail for better analysis
            },
          })),
        ];
      } else {
        userMessageContent = enhancedMessage;
      }

      // Add user message to history
      session.messages.push({
        role: 'user',
        content: userMessageContent,
      });

      // Keep only last 10 messages (ConversationBufferWindowMemory with k=10)
      if (session.messages.length > this.maxMessages * 2) {
        session.messages = session.messages.slice(-this.maxMessages * 2);
      }

      // Prepare messages for API call
      const apiMessages = [
        { role: 'system', content: this.getSystemPrompt() },
        ...session.messages,
      ];

      // Call OpenAI API via Firebase Function
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      const token = await user.getIdToken();

      const response = await fetch(`${FUNCTIONS_URL}/openaiChatCompletion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: apiMessages,
          temperature: 0.7,
          max_tokens: 800
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to get response');
      }

      const assistantResponse = result.response;

      // Add assistant response to history
      session.messages.push({
        role: 'assistant',
        content: assistantResponse,
      });

      return {
        success: true,
        response: assistantResponse,
        timestamp: new Date(),
      };
    } catch (error) {
      console.error('MahStry AI Tutor Error:', error);
      return {
        success: false,
        error: error.message || 'Failed to get response from AI Tutor',
        timestamp: new Date(),
      };
    }
  }

  // Get conversation history
  getHistory(sessionId) {
    const session = this.conversations.get(sessionId);
    if (!session) {
      return [];
    }
    return session.messages || [];
  }

  // Clear conversation history
  clearHistory(sessionId) {
    const session = this.conversations.get(sessionId);
    if (session) {
      session.messages = [];
    }
  }

  // Delete a session
  deleteSession(sessionId) {
    this.conversations.delete(sessionId);
  }

  // Clean up old sessions (older than 24 hours)
  cleanupOldSessions() {
    const now = new Date();
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours

    for (const [sessionId, session] of this.conversations.entries()) {
      if (now - session.lastActivity > maxAge) {
        this.conversations.delete(sessionId);
      }
    }
  }
}

// Export singleton instance
export const mahstryAITutor = new MahStryAITutorService();
export default mahstryAITutor;
